/**
 * Design tokens shared by every Tarabeeza client. Web apps map these into
 * Tailwind/CSS variables; React Native apps consume them directly. Keeping one
 * source means a table that is "occupied" looks identical on POS, waiter and map.
 */

export * from './brand';

import { brandRamp } from './brand';

export const colors = {
  /**
   * The Tarabeeza amber, replacing the blue this shipped with. 500 is the brand colour exactly;
   * see `brand.ts` for how the ends were derived.
   *
   * ⚠️ `warning` below is still amber (#f59e0b, hue ~38°) and the brand is now hue ~36°, so a
   * warning badge no longer separates from brand chrome by hue alone. That matters on the KDS,
   * where 17 of the product's badges are warnings and a late ticket has to shout. Left as-is
   * deliberately: moving a semantic colour changes what existing screens MEAN.
   */
  brand: brandRamp,
  neutral: {
    50: '#f8fafc', 100: '#f1f5f9', 200: '#e2e8f0', 300: '#cbd5e1',
    400: '#94a3b8', 500: '#64748b', 600: '#475569', 700: '#334155',
    800: '#1e293b', 900: '#0f172a', 950: '#020617',
  },
  success: '#16a34a',
  warning: '#f59e0b',
  danger: '#dc2626',
  info: '#0ea5e9',
} as const;

/** §7 — the table lifecycle, colour-coded identically everywhere. */
export const tableStateColors = {
  AVAILABLE: '#16a34a',
  OCCUPIED: '#1d66f0',
  PAYMENT_PENDING: '#f59e0b',
  CLEANING: '#0ea5e9',
  RESERVED: '#8b5cf6',
  BLOCKED: '#64748b',
  OUT_OF_SERVICE: '#dc2626',
} as const;

/**
 * ADR-005 — the floor map palette, one element type to one fill.
 *
 * Here rather than in either client: the dashboard and the waiter app draw the same room, and a
 * room that is a different colour depending on which screen you look at is a room nobody trusts.
 */
export const mapElementColors = {
  TABLE: colors.brand[300],
  RESOURCE: colors.brand[200],
  WALL: colors.neutral[400],
  DOOR: colors.neutral[300],
  WINDOW: colors.info,
  BAR: colors.neutral[500],
  KITCHEN: colors.neutral[200],
  CASHIER: colors.warning,
  BATHROOM: colors.neutral[300],
  ENTRANCE: colors.success,
  STAIRS: colors.neutral[400],
  TEXT: colors.neutral[100],
  CUSTOM: colors.neutral[200],
} as const;

/** A table's fill is its live state (§7); an unknown state reads as blocked. */
export function tableFill(state: string | undefined): string {
  return (
    tableStateColors[state as keyof typeof tableStateColors] ?? tableStateColors.BLOCKED
  );
}

export const resourceStatusColors = {
  AVAILABLE: '#16a34a',
  RESERVED: '#8b5cf6',
  IN_USE: '#1d66f0',
  PAUSED: '#f59e0b',
  MAINTENANCE: '#64748b',
  OUT_OF_SERVICE: '#dc2626',
} as const;

/** §15 — customer-visible order journey. */
export const orderStatusColors = {
  ORDER_RECEIVED: '#64748b',
  CONFIRMED: '#0ea5e9',
  PREPARING: '#f59e0b',
  PARTIALLY_READY: '#f97316',
  READY: '#16a34a',
  SERVED: '#1d66f0',
  PICKED_UP: '#1d66f0',
  COMPLETED: '#475569',
} as const;

/**
 * The spacing ramp. 4-based, with the two half-steps the dashboard actually
 * reaches for and the card-interior step it pads with.
 *
 * `hair`, `snug` and `pane` were added in 1.1.0 rather than renumbering the
 * scale: `xs`..`3xl` are addressed by name in four apps and moving one would
 * silently reflow every screen that reads it.
 */
export const spacing = {
  /** The 2px step: a required-field star, a hint's offset from its label. */
  hair: 2,
  xs: 4,
  /** The 6px step: the gap inside a label-and-control pair. */
  snug: 6,
  sm: 8,
  md: 12,
  lg: 16,
  /** Card, dialog and sheet interior padding. */
  pane: 20,
  xl: 24, '2xl': 32, '3xl': 48,
} as const;

/**
 * The legacy radius scale.
 *
 * Kept exactly as it was — three apps address `radius.md` and `radius.lg` — but
 * it is not the dashboard's, and `radii` below is. New work uses `radii`.
 */
export const radius = {
  sm: 6, md: 10, lg: 14, xl: 20, full: 9999,
} as const;

/**
 * Radius, as the dashboard actually uses it: three values, not five.
 *
 * Counted across the dashboard's source — `rounded-lg` 177 times, `rounded-full`
 * 45, `rounded-xl` 27, everything else in the noise. So the system is: 8 on
 * anything you interact with, 12 on anything that contains, a pill on anything
 * round. The five-step ramp above was a scale nobody was using as a scale.
 */
export const radii = {
  /** Buttons, inputs, selects, chips-with-corners, small hit areas. */
  control: 8,
  /** Cards, tables, dialogs, popovers, sheets. */
  container: 12,
  /** Badges, avatars, dots, switch tracks. */
  pill: 9999,
} as const;

/**
 * Control heights.
 *
 * 44 is the default and also `touchTarget.min`, which is not a coincidence: the
 * dashboard is operated on laptops in a venue office and on tablets on a floor,
 * so its baseline control is already a touch target. 36 is the compact size the
 * dashboard reaches for most often in toolbars; 56 is `touchTarget.comfortable`,
 * for staff surfaces where the hand is not steady.
 */
export const controlHeight = {
  compact: 36,
  default: 44,
  tablet: 56,
} as const;

/**
 * Elevation is a **border**, not a shadow.
 *
 * The dashboard draws 409 borders and 7 shadows. Surfaces are flat and
 * separated by a 1px hairline; a shadow appears only on something that
 * genuinely floats above the page — a dialog, a popover, a context menu. Two
 * levels is the whole vocabulary.
 */
export const borderWidth = {
  /** Every surface edge and every rule. */
  hairline: 1,
  /** A selected chip, a tab indicator, the focus ring. */
  emphasis: 2,
} as const;

/**
 * The focus indicator: 2px, drawn flush.
 *
 * No offset anywhere in the dashboard — the ring sits on the control's own
 * edge. Web-only: there is no `:focus-visible` on a touch surface, so the
 * native apps re-express this as a pressed and a selected state instead.
 */
export const focusRing = { width: 2, offset: 0 } as const;

/**
 * Motion, of which there is almost none.
 *
 * The dashboard animates colour and nothing else, at Tailwind's default 150ms.
 * The two exceptions are both layout widths. A staff app that grows its own
 * transition vocabulary is diverging.
 */
export const duration = { fast: 150, base: 200 } as const;

/**
 * The two shadows, in the two forms the fleet needs.
 *
 * Not portable as one value: CSS takes a `box-shadow` string and React Native
 * takes four separate iOS properties plus an Android `elevation` integer, and
 * the two do not render identically however carefully they are matched. Both
 * spellings live here so at least they are one *decision*.
 */
export const elevation = {
  /** A card's whisper. Tailwind `shadow-sm`. */
  raised: {
    web: '0 1px 3px rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
    native: {
      shadowColor: '#000000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.1,
      shadowRadius: 3,
      elevation: 2,
    },
  },
  /** A dialog, a popover, a context menu. Tailwind `shadow-xl`. */
  floating: {
    web: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
    native: {
      shadowColor: '#000000',
      shadowOffset: { width: 0, height: 12 },
      shadowOpacity: 0.24,
      shadowRadius: 20,
      elevation: 16,
    },
  },
} as const;

/**
 * The modal scrim, as `#rrggbbaa` — the one notation both CSS and React Native
 * parse. The dashboard paints `bg-black/40`; 0.4 alpha is `66`.
 */
export const scrim = '#00000066';

/** Paired with `lineHeight` in ./typography, step for step. */
export const fontSize = {
  xs: 12, sm: 14, base: 16, lg: 18, xl: 20, '2xl': 24, '3xl': 30, '4xl': 36,
} as const;

/**
 * §25 — Arabic needs a face with real Arabic coverage; keep the pairing here.
 *
 * CSS stacks, and therefore web-only. Re-exported unchanged as `fontStack` in
 * ./typography, which also carries `nativeFontFamily` — the same pairing under
 * the per-weight family names React Native has to be given instead.
 */
export const fontFamily = {
  ltr: "'Inter', system-ui, -apple-system, sans-serif",
  rtl: "'IBM Plex Sans Arabic', 'Noto Kufi Arabic', system-ui, sans-serif",
} as const;

/** Touch targets for tablet-first staff apps (waiter, POS, KDS). */
export const touchTarget = { min: 44, comfortable: 56, large: 72 } as const;

export type TableStateColor = keyof typeof tableStateColors;
export type ResourceStatusColor = keyof typeof resourceStatusColors;
export type OrderStatusColor = keyof typeof orderStatusColors;
